import {useGetAutoDeliveryDetailsExtendedDcx} from '@/api/consumerApi';
import {AppButton} from '@/components/Button';
import appConfig from '@/config/appConfig.typed';
import {DEFAULT_WEEKEND_DAYS, mapDayOfWeekListToNumbers} from '@/config/types/calendarSettingsModel';
import {AutoDeliveryFrequency} from '@/consts/icc/iccAutoDeliveryStatusTypes';
import stringUtils from '@/utils/stringUtils';

import {getRescheduleContent} from '../utils/getRescheduleContent';
import {getNextOrderRange} from '../utils/rescheduleDateRange';
import RescheduleSelectionSummaryCard from './RescheduleSelectionSummaryCard';

type RescheduleConfirmationStepProps = {
    selectedFrequency: AutoDeliveryFrequency | null;
    selectedDate: Date;
    onConfirm: () => void;
    isLoading?: boolean;
    titleId: string;
};

const RescheduleConfirmationStep = ({
    selectedFrequency,
    selectedDate,
    onConfirm,
    isLoading,
    titleId,
}: RescheduleConfirmationStepProps) => {
    const {excludeWeekends = false} = appConfig.getAutoDeliverySettings() || {};
    const {weekendDays} = appConfig.getCalendarSettings() || {};
    const rescheduleDeliveryContent = getRescheduleContent();
    const {confirmationContent} = rescheduleDeliveryContent || {};
    const {title, description, confirmButton} = confirmationContent ?? {};
    const {data} = useGetAutoDeliveryDetailsExtendedDcx();
    const {minDeliveryDaysCount, maxDeliveryDaysCount} = data?.autoDelivery || {};
    const weekendDaysNumbers = mapDayOfWeekListToNumbers(weekendDays ?? DEFAULT_WEEKEND_DAYS);
    const {minDate: nextOrderMinDate, maxDate: nextOrderMaxDate} = getNextOrderRange({
        selectedDate,
        minDeliveryDaysCount,
        maxDeliveryDaysCount,
        excludeWeekends,
        weekendDaysNumbers,
    });
    const formattedDescription =
        description && Number.isFinite(minDeliveryDaysCount) && Number.isFinite(maxDeliveryDaysCount)
            ? stringUtils.formatString(description, minDeliveryDaysCount, maxDeliveryDaysCount)
            : description;

    return (
        <div className='flex flex-col gap-4'>
            <h2 id={titleId} className='mb-0 text-28'>
                {title}
            </h2>
            <RescheduleSelectionSummaryCard
                selectedFrequency={selectedFrequency}
                minDate={nextOrderMinDate}
                maxDate={nextOrderMaxDate}
            />
            {formattedDescription && <p className='text-16 leading-15'>{formattedDescription}</p>}
            {confirmButton?.text && (
                <AppButton
                    primary
                    light
                    onClick={onConfirm}
                    loading={isLoading}
                    className='m-0'
                    aria-label={confirmButton.alt}
                >
                    {confirmButton.text}
                </AppButton>
            )}
        </div>
    );
};

export default RescheduleConfirmationStep;
